/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { ArrowCounterClockwise, Plus } from "@phosphor-icons/react"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useMemo, useRef, useState } from "react"

import { Button, IconButton } from "~/components/design-system/components"
import {
  FlagSelect,
  keepSelectKeys,
  SearchInput,
} from "~/features/feature-flags/client/FlagControls"
import {
  useFlagPanel,
  useRequestFlagChange,
} from "~/features/feature-flags/client/FlagPanelProvider"
import { FlagStatusBadge } from "~/features/feature-flags/client/FlagStatusBadge"
import { AddUserOverrideDialog } from "~/features/feature-flags/client/flag-page/AddUserOverrideDialog"
import { FlagPageWorkspace } from "~/features/feature-flags/client/flag-page/FlagPageWorkspace"
import { useFlagPageKey } from "~/features/feature-flags/client/flag-page/useFlagPageKey"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { targetsRequest } from "~/features/feature-flags/client/model/changeRequest"
import { NO_MATCHES } from "~/features/feature-flags/client/model/filters"
import {
  filterUserRows,
  flagUserRows,
  type FlagUserRow,
} from "~/features/feature-flags/client/model/flagPage"
import { FLAG_STATE_ORDER } from "~/features/feature-flags/client/model/status"

const usersRoute = getRouteApi("/_auth/internal/feature-flags/$flagKey/_layout/users")

function StatusCell({ data }: ICellRendererParams<FlagUserRow>) {
  return data ? <FlagStatusBadge status={data} /> : null
}

function SettingCell({ data }: ICellRendererParams<FlagUserRow>) {
  const flagKey = useFlagPageKey()
  const requestChange = useRequestFlagChange()
  if (!data) return null
  return (
    <FlagSelect
      ariaLabel={`Setting for ${data.user?.name ?? data.id}`}
      size="small"
      value={data.value}
      onChange={(value) => requestChange(targetsRequest("user", flagKey, data.id, value))}
    />
  )
}

function ResetCell({ data }: ICellRendererParams<FlagUserRow>) {
  const flagKey = useFlagPageKey()
  const requestChange = useRequestFlagChange()
  if (!data) return null
  return (
    <IconButton
      variant="transparent"
      size="xs"
      requires="staff"
      allowedTitle="Reset override. The user gets their company's setting again."
      aria-label={`Reset override for ${data.user?.name ?? data.id}`}
      onClick={() => requestChange(targetsRequest("user", flagKey, data.id, null))}
    >
      <ArrowCounterClockwise />
    </IconButton>
  )
}

/** Who set the override, then when, muted. */
function SetByCell({ data }: ICellRendererParams<FlagUserRow>) {
  const panel = useFlagPanel()
  if (!data) return null
  return (
    <span className="flex min-w-0 items-center gap-1">
      {panel.userName(data.updatedBy)}
      <span className="shrink-0 text-fg-muted">
        · {formatDate(data.updatedAt, DateFormatType.FullDateShort)}
      </span>
    </span>
  )
}

const USER_COLUMNS: ColDef<FlagUserRow>[] = [
  {
    colId: "user",
    headerName: "User",
    valueGetter: ({ data }) => (data ? (data.user?.name ?? data.id) : ""),
    tooltip: ({ data }) => data?.user?.email,
    minWidth: 140,
    flex: 1,
  },
  {
    colId: "companies",
    headerName: "Company",
    field: "companies",
    tooltip: ({ data }) => data?.companies,
    minWidth: 90,
    flex: 1,
  },
  {
    colId: "status",
    headerName: "Status",
    valueGetter: ({ data }) => (data ? FLAG_STATE_ORDER[data.state] : undefined),
    cellRenderer: StatusCell,
    cellClass: "ag-cell-vcenter",
    width: 110,
  },
  {
    colId: "setting",
    headerName: "Setting",
    cellRenderer: SettingCell,
    cellClass: "ag-cell-vcenter",
    suppressKeyboardEvent: keepSelectKeys,
    sortable: false,
    width: 110,
  },
  {
    colId: "note",
    headerName: "Note",
    field: "note",
    tooltip: ({ data }) => data?.note,
    minWidth: 80,
    flex: 1,
  },
  {
    colId: "setBy",
    headerName: "Set by",
    valueGetter: ({ data }) => data?.updatedAt,
    cellRenderer: SetByCell,
    width: 200,
  },
  {
    colId: "remove",
    headerName: "",
    cellRenderer: ResetCell,
    cellClass: "ag-cell-vcenter",
    sortable: false,
    width: 48,
  },
]

/** The flag's user overrides, with Add and Remove. */
export function FlagUsersTab() {
  const flagKey = useFlagPageKey()
  const { q: search = "" } = usersRoute.useSearch()
  const navigate = useNavigate()
  const panel = useFlagPanel()
  const gridRef = useRef<AgGridReact<FlagUserRow>>(null)
  const requestChange = useRequestFlagChange()
  const [adding, setAdding] = useState(false)

  const setSearch = (next: string) =>
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, q: next || undefined }),
      replace: true,
    })

  const allRows = useMemo(() => flagUserRows(panel, flagKey), [panel, flagKey])
  const rows = useMemo(() => filterUserRows(allRows, search), [allRows, search])

  return (
    <FlagPageWorkspace
      toolbar={
        <div className="flex w-full min-w-0 flex-wrap items-center gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search users" />
          <Button
            variant="neutral"
            size="sm"
            requires="staff"
            wrapperClassName="ml-auto"
            onClick={() => setAdding(true)}
          >
            <Plus />
            Add user override
          </Button>
        </div>
      }
    >
      <DataTableBaseV2<FlagUserRow>
        id={`feature-flag-users-${flagKey}`}
        gridRef={gridRef}
        rowData={rows}
        rowIdField="id"
        columnDefs={USER_COLUMNS}
        loading={panel.loadingCompanies || panel.loadingUsers}
        showPagination={false}
        compact
        disableColumnSizingPersistence
        emptyMessage={search ? NO_MATCHES : "No user overrides"}
      />
      <AddUserOverrideDialog
        open={adding}
        onOpenChange={setAdding}
        flagKey={flagKey}
        onPick={(userId, value) => requestChange(targetsRequest("user", flagKey, userId, value))}
      />
    </FlagPageWorkspace>
  )
}
